/**
 * SVG pre-rasterization utilities for PNG export.
 *
 * html2canvas(-pro) cannot faithfully render hand-authored SVG elements whose
 * fills/strokes come from CSS rather than inline SVG attributes. The fix:
 * rasterize the SVG natively (XMLSerializer → canvas → dataURL) before
 * html2canvas runs, then use the onclone hook to swap the live <svg> for a
 * flat <img> — so html2canvas never has to render the SVG itself.
 *
 * Pattern mirrors CopyPngButton in features/dashboards-v2 (the original site).
 * Charts that contain a [data-chart-export-root="true"] marker signal that
 * their SVG needs this treatment; all other charts return null and are unaffected.
 */

// Poppins weight 400 (latin subset) — embedded as base64 so SVG text renders
// with the correct font inside the isolated Blob-URL rasterization context
// where page web fonts are unavailable.
import poppins400Url from '@fontsource/poppins/files/poppins-latin-400-normal.woff2'

// Trimmed style whitelist — excludes font properties; font is handled via the
// embedded @font-face block injected into the SVG <defs> instead.
const SVG_STYLE_PROPERTIES = [
  'fill',
  'stroke',
  'stroke-width',
  'stroke-dasharray',
  'stroke-linecap',
  'stroke-linejoin',
  'fill-opacity',
  'stroke-opacity',
  'opacity',
  'text-anchor',
  'dominant-baseline',
]

const inlineStyles = (element: Element): void => {
  const computedStyle = window.getComputedStyle(element)
  SVG_STYLE_PROPERTIES.forEach((prop) => {
    const value = computedStyle.getPropertyValue(prop)
    if (value) {
      ;(element as HTMLElement).style.setProperty(prop, value)
    }
  })
  Array.from(element.children).forEach((child) => {
    inlineStyles(child)
  })
}

/** Fetch a font file and return its contents as a base64 string. */
const fetchFontBase64 = async (url: string): Promise<string> => {
  const response = await fetch(url)
  const buffer = await response.arrayBuffer()
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]!)
  }
  return btoa(binary)
}

/**
 * If `wrapperNode` contains a [data-chart-export-root="true"] element with an
 * <svg> child, pre-rasterizes that SVG to a PNG dataURL and returns a
 * synchronous onclone callback for html2canvas that swaps the cloned <svg>
 * with an <img> of the pre-rasterized PNG.
 *
 * Returns null when no [data-chart-export-root] marker is found — zero cost
 * for charts that don't need this treatment.
 */
export const buildSvgOnClone = async (
  wrapperNode: HTMLElement,
): Promise<
  ((clonedDoc: Document, clonedElement: HTMLElement) => void) | null
> => {
  const exportRoot = wrapperNode.querySelector(
    '[data-chart-export-root="true"]',
  )
  const svg = exportRoot?.querySelector('svg')
  if (!svg) return null

  const rect = svg.getBoundingClientRect()
  const svgClone = svg.cloneNode(true) as SVGSVGElement
  svgClone.setAttribute('width', String(rect.width))
  svgClone.setAttribute('height', String(rect.height))
  inlineStyles(svgClone)

  // Embed Poppins so SVG <text> elements render with the correct font inside
  // the isolated Blob-URL context (no page web fonts available there).
  const poppinsBase64 = await fetchFontBase64(poppins400Url)
  const fontFaceCSS = `
    @font-face {
      font-family: 'Poppins';
      font-weight: 400;
      font-style: normal;
      src: url('data:font/woff2;base64,${poppinsBase64}') format('woff2');
    }
    text { font-family: 'Poppins', sans-serif; }
  `
  let defs = svgClone.querySelector('defs')
  if (!defs) {
    defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
    svgClone.insertBefore(defs, svgClone.firstChild)
  }
  const styleEl = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'style',
  )
  styleEl.textContent = fontFaceCSS
  defs.appendChild(styleEl)

  const serializer = new XMLSerializer()
  const rawSvgString = serializer.serializeToString(svgClone)
  // Defensively strip C0 control chars: they're invalid in XML 1.0 and make the
  // serialized SVG fail to load as an image. Chart SVGs shouldn't contain them
  // (CustomHeatmap's hover-key separator is an XML-valid U+2063), but this keeps
  // any stray control char from silently dropping the chart from export.
  // Preserve valid XML whitespace: tab (\x09), LF (\x0A), CR (\x0D).
  // eslint-disable-next-line no-control-regex -- permanent: stripping C0 control chars (invalid XML 1.0) requires matching them; see rationale above.
  const svgString = rawSvgString.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '')
  const svgBlob = new Blob(
    ['<?xml version="1.0" standalone="no"?>\r\n' + svgString],
    { type: 'image/svg+xml;charset=utf-8' },
  )
  const url = URL.createObjectURL(svgBlob)

  // Rasterize: load SVG into an <img>, draw onto a 2× canvas, extract PNG.
  // drawImage is called inside onload so the canvas-image-without-draw rule is satisfied.
  const scale = 2
  const rasterizedPng = await new Promise<string | null>((resolve) => {
    const svgCanvas = document.createElement('canvas')
    svgCanvas.width = rect.width * scale
    svgCanvas.height = rect.height * scale
    const ctx = svgCanvas.getContext('2d')
    if (!ctx) {
      URL.revokeObjectURL(url)
      resolve(null)
      return
    }

    const img = new Image()
    img.onload = () => {
      ctx.fillStyle = 'white'
      ctx.fillRect(0, 0, svgCanvas.width, svgCanvas.height)
      ctx.scale(scale, scale)
      ctx.drawImage(img, 0, 0)
      URL.revokeObjectURL(url)
      resolve(svgCanvas.toDataURL('image/png'))
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(null)
    }
    img.src = url
  })

  if (!rasterizedPng) return null

  // Return a synchronous onclone callback — the async work is already done.
  // Named function expression: react/display-name misreads the anonymous
  // higher-order return as a component; the name also aids stack traces.
  return function svgOnClone(
    clonedDoc: Document,
    clonedElement: HTMLElement,
  ): void {
    const clonedRoot = clonedElement.querySelector(
      '[data-chart-export-root="true"]',
    )
    const clonedSvg = clonedRoot?.querySelector('svg')
    if (clonedSvg?.parentNode) {
      const imgEl = clonedDoc.createElement('img')
      imgEl.src = rasterizedPng
      imgEl.width = rect.width
      imgEl.height = rect.height
      imgEl.style.display = 'block'
      imgEl.style.width = `${rect.width}px`
      imgEl.style.height = `${rect.height}px`
      clonedSvg.parentNode.replaceChild(imgEl, clonedSvg)
    }
  }
}
